import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class AssemblyQcMeasurementLineReadOnly extends ModelObject<AssemblyQcMeasurementLineReadOnly> {

    /**
    * Measurement set of which this measurement is a part.
    */
    public assemblyQcMeasurementId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyQcMeasurementId = Guid.parseOrEmpty(jsonSerializedObject.assemblyQcMeasurementId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyQcMeasurementId = this.assemblyQcMeasurementId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyQcMeasurementId", rule: (v: any) => !v.isEmpty() || "AssemblyQcMeasurementLineReadOnly.AssemblyQcMeasurementId cannot be empty"},
    ];
}


export class AssemblyQcMeasurementLine extends ModelObject<AssemblyQcMeasurementLine> {

    /**
    * Assembly test metric which has been measured.
    */
    public assemblyQcTestMetricLineId: Guid = Guid.createEmpty();
    private _value: number | null = null;
    /**
    * The actual value measured. Units as specified in the assembly qc test metric.
    */
    public get value(): number | null { return this._value }
    public set value(value: number | null) {
      this._value = value === undefined ? null : value;
    }
    private _valueString: string | null = null;
    /**
    * The actual value measured. Free text input with no units or expectations.
    * Nullable
    * Preserves leading and trailing whitespace.
    */
    public get valueString(): string | null { return this._valueString }
    public set valueString(value: string | null) {
      this._valueString = value === undefined ? null : value;
    }
    private _valueBoolean: boolean | null = null;
    /**
    * The actual value measured. Boolean input with no units or expectations.
    */
    public get valueBoolean(): boolean | null { return this._valueBoolean }
    public set valueBoolean(value: boolean | null) {
      this._valueBoolean = value === undefined ? null : value;
    }
    public readOnly: AssemblyQcMeasurementLineReadOnly = new AssemblyQcMeasurementLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyQcTestMetricLineId = Guid.parseOrEmpty(jsonSerializedObject.assemblyQcTestMetricLineId);
          this.value = jsonSerializedObject.value == null ? null : jsonSerializedObject.value;
          this.valueString = jsonSerializedObject.valueString == null ? null : jsonSerializedObject.valueString;
          this.valueBoolean = jsonSerializedObject.valueBoolean == null ? null : jsonSerializedObject.valueBoolean;
          this.readOnly = new AssemblyQcMeasurementLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyQcTestMetricLineId = this.assemblyQcTestMetricLineId.toString();
        toRet.value = this.value == null ? null : this.value;
        toRet.valueString = this.valueString == null ? null : this.valueString;
        toRet.valueBoolean = this.valueBoolean == null ? null : this.valueBoolean;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyQcTestMetricLineId", rule: (v: any) => !v.isEmpty() || "AssemblyQcMeasurementLine.AssemblyQcTestMetricLineId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyQcMeasurementHeader extends ModelObject<AssemblyQcMeasurementHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Assembly qc test set being measured.
    */
    public assemblyQcTestId: Guid = Guid.createEmpty();
    /**
    * Timestamp of when measurement was taken.
    */
    public measuredAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public notes: string = "";
    /**
    * User who performed the tests. Note that this is not always the user who put the record on the system, therefore it is automatically filled in by the system and therefore is not an audit showing proof of who did it.
    */
    public userId: Guid = Guid.createEmpty();
    private _vesselId: Guid | null = null;
    /**
    * Vessel for which the measurements were made (if the measurements are vessel-based).
    */
    public get vesselId(): Guid | null { return this._vesselId }
    public set vesselId(value: Guid | null) {
      this._vesselId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.assemblyQcTestId = Guid.parseOrEmpty(jsonSerializedObject.assemblyQcTestId);
          this.measuredAt = Instant.parse(jsonSerializedObject.measuredAt);
          this.notes = jsonSerializedObject.notes;
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.vesselId = jsonSerializedObject.vesselId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.vesselId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.assemblyQcTestId = this.assemblyQcTestId.toString();
        toRet.measuredAt = this.measuredAt.toString();
        toRet.notes = this.notes;
        toRet.userId = this.userId.toString();
        toRet.vesselId = this.vesselId == null ? null : this.vesselId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyQcMeasurementHeader.Id cannot be empty"},
            {field: "assemblyQcTestId", rule: (v: any) => !v.isEmpty() || "AssemblyQcMeasurementHeader.AssemblyQcTestId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "AssemblyQcMeasurementHeader.UserId cannot be empty"},
            {field: "vesselId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyQcMeasurementHeader.VesselId cannot be empty"},
    ];
}


export class AssemblyQcMeasurement extends ModelObject<AssemblyQcMeasurement> {

    public header: AssemblyQcMeasurementHeader = new AssemblyQcMeasurementHeader();
    public lines: (AssemblyQcMeasurementLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new AssemblyQcMeasurementHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new AssemblyQcMeasurementLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyQcMeasurementLine[]).reduce((acc: Array<string>, curr: AssemblyQcMeasurementLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AssemblyQcMeasurementService extends ServiceBase {

  public async getAssemblyQcMeasurement(assemblyQcMeasurementId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyQcMeasurement> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcMeasurementId:assemblyQcMeasurementId.toString(),
    };
    const extractor = (response:any) => new AssemblyQcMeasurement(response.assemblyQcMeasurement);;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurement", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForAssembly(assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForAssembly", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForAssemblyQcTest(assemblyQcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestId:assemblyQcTestId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForAssemblyQcTest", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForAssemblyQcTests(assemblyQcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcTestIds:assemblyQcTestIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForAssemblyQcTests", req, false, extractor, opts);
  };

  public async saveAssemblyQcMeasurement(assemblyQcMeasurement: AssemblyQcMeasurement, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcMeasurement:assemblyQcMeasurement.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/saveAssemblyQcMeasurement", req, true, extractor, opts);
  };

  public async deleteAssemblyQcMeasurement(assemblyQcMeasurementId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyQcMeasurementId:assemblyQcMeasurementId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/deleteAssemblyQcMeasurement", req, true, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForProcess(processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForProcess", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForBatch(batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForBatch", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForAssemblies(assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForAssemblies", req, false, extractor, opts);
  };

  public async getAssemblyQcMeasurementsForProcesses(processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AssemblyQcMeasurement)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyQcMeasurement.map((v: any) => new AssemblyQcMeasurement(v));;
    return this.makeJsonRequest("assemblyQcMeasurement/v1/getAssemblyQcMeasurementsForProcesses", req, false, extractor, opts);
  };
}

export const assemblyQcMeasurementService = new AssemblyQcMeasurementService();